import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUser } from "@/contexts/user";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isAuthenticated = !!user?.id;

  return (
    <div className="min-h-screen bg-background p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(isAuthenticated ? "/profile" : "/auth")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isAuthenticated ? "Back to Profile" : "Back to Login"}
        </Button>
        <Card className="p-8">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <ScrollArea className="h-[calc(100vh-250px)]">
            <div className="space-y-6 text-muted-foreground pr-4">
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">1. Information We Collect</h2>
                <p>StudyForge collects the following information:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Email address (for account creation and authentication)</li>
                  <li>Study session data (duration, timestamps)</li>
                  <li>Avatar selection and profile customization</li>
                  <li>Location data (country, state, city - optional)</li>
                  <li>Usage analytics (page views, feature interactions)</li>
                  <li>Payment information (processed securely via Stripe - we never store card details)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">2. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Provide and improve our study tracking service</li>
                  <li>Display you on leaderboards (if opted in)</li>
                  <li>Send email reminders and notifications (if opted in)</li>
                  <li>Analyze usage patterns to improve the app</li>
                  <li>Maintain your account and preferences</li>
                  <li>Process payments for Premium subscriptions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">3. Third-Party Services</h2>
                <p>We use the following third-party services:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Supabase</strong> - Database and authentication</li>
                  <li><strong>Stripe</strong> - Secure payment processing</li>
                  <li><strong>Resend</strong> - Email notifications</li>
                  <li><strong>PostHog</strong> - Analytics (anonymized)</li>
                </ul>
                <p className="mt-2">
                  Each of these services has their own privacy policy and security measures. 
                  We only share the minimum data necessary for each service to function.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">4. Data Security</h2>
                <p>We implement industry-standard security measures including:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Encrypted data transmission (HTTPS)</li>
                  <li>Secure password hashing</li>
                  <li>Row-level security on all database tables</li>
                  <li>Regular security audits</li>
                  <li>PCI-compliant payment processing via Stripe</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">5. Your Rights (GDPR/CCPA)</h2>
                <p>You have the right to:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Access</strong> your data - View your profile and study history in-app</li>
                  <li><strong>Delete</strong> your data - Use the account deletion feature in settings</li>
                  <li><strong>Export</strong> your data - Contact us for a data export</li>
                  <li><strong>Opt-out</strong> of emails - Toggle email notifications in profile settings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">6. Data Retention</h2>
                <p>We retain your data for as long as your account is active. Upon account deletion, all personal data is permanently removed within 30 days.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">7. Contact Us</h2>
                <p>For privacy concerns or data requests, use the feedback feature in the app or email us at privacy@studyforge.app</p>
              </section>

              <p className="text-sm mt-8 pt-6 border-t">
                Last updated: January 2025
              </p>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
